import { PostHogProvider } from '@posthog/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { registerBillingQueryClient } from '@/lib/billing/use-subscription';

const POSTHOG_OPTIONS = {
  api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
  defaults: '2026-01-30',
  session_recording: {
    maskAllInputs: true,
  },
  person_profiles: 'identified_only',
} as const;

const queryClient = new QueryClient();

// Non-React code paths (entity events, the assistant transport) invalidate the
// credit balance through this registration when work that spends credits ends.
registerBillingQueryClient(queryClient);

/**
 * AppProvider — top-level provider stack that wraps the router.
 *
 * Workspace-scoped providers (SyncSystemProvider, WorkspaceServiceProvider,
 * SnapshotServiceProvider, SessionProvider) live inside MainLayout (a route
 * component) because they are keyed on activeWorkspaceId and must remount on
 * workspace switch. Any truly global providers (e.g. future theme, analytics
 * context) belong here.
 */
export function AppProvider({ children }: { children: ReactNode }) {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const withQuery = <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
  if (!apiKey) return withQuery;
  return (
    <PostHogProvider apiKey={apiKey} options={POSTHOG_OPTIONS}>
      {withQuery}
    </PostHogProvider>
  );
}
